import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function startCheckout(lookupKey, { folio, address } = {}, t) {
  try {
    const { data } = await axios.post(`${API}/payments/checkout`, {
      lookup_key: lookupKey,
      origin_url: window.location.origin,
      folio,
      address,
    });
    if (data.checkout_url) {
      window.location.href = data.checkout_url;
    }
  } catch {
    toast.error(t ? t("pay.startErr") : "Could not start checkout. Please try again.");
  }
}

export function useEntitlement(folio, lookupKey) {
  const [unlocked, setUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(() => {
    if (!folio) {
      setUnlocked(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${API}/payments/entitlement`, { params: { folio, lookup_key: lookupKey } })
      .then((r) => setUnlocked(!!r.data.unlocked))
      .catch(() => setUnlocked(false))
      .finally(() => setLoading(false));
  }, [folio, lookupKey]);
  useEffect(() => {
    refresh();
  }, [refresh]);
  return { unlocked, loading, refresh };
}

export const PaymentResult = ({ mode, onDone }) => {
  const { t } = useLang();
  const [state, setState] = useState(mode === "cancel" ? "cancelled" : "processing");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (mode === "cancel") return;
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo(data);
          setState("success");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("failed");
          return;
        }
      } catch {
        /* keep trying */
      }
      if (attempts >= 20) {
        setState("failed");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [mode]);

  return (
    <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-24 text-center" data-testid="payment-result">
      {state === "processing" && (
        <>
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <p className="mt-5 font-serif text-2xl font-semibold text-foreground">{t("pay.processing")}</p>
        </>
      )}
      {state === "success" && (
        <>
          <CheckCircle2 className="h-14 w-14 text-emerald-500" data-testid="payment-success-icon" />
          <p className="mt-5 font-serif text-3xl font-semibold text-foreground">{t("pay.success")}</p>
          <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            {t("pay.successDesc", { product: info?.product_label || "" })}
          </p>
          <Button className="mt-7" data-testid="payment-continue-btn" onClick={onDone}>
            {t("pay.continue")}
          </Button>
        </>
      )}
      {state === "cancelled" && (
        <>
          <XCircle className="h-14 w-14 text-muted-foreground" />
          <p className="mt-5 font-serif text-3xl font-semibold text-foreground">{t("pay.cancelled")}</p>
          <p className="mt-2 text-sm text-muted-foreground">{t("pay.cancelledDesc")}</p>
          <Button variant="outline" className="mt-7" data-testid="payment-back-btn" onClick={onDone}>
            {t("pay.back")}
          </Button>
        </>
      )}
      {state === "failed" && (
        <>
          <XCircle className="h-14 w-14 text-destructive" />
          <p className="mt-5 font-serif text-3xl font-semibold text-foreground">{t("pay.failed")}</p>
          <Button variant="outline" className="mt-7" data-testid="payment-back-btn" onClick={onDone}>
            {t("pay.back")}
          </Button>
        </>
      )}
    </div>
  );
};
